import {Component, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {Subject, finalize, takeUntil} from 'rxjs';
import {ProdutoVM} from 'src/app/api/model/produto/produtoVM';
import {TipoProduto_E_OperadoraEnum} from 'src/app/api/module/erp/shared/enumerable/tipo-produto.enum';
import {CompartilharDadosEntreComponentsService} from 'src/app/api/module/erp/shared/services/compartilhar-dados-entre-components.service';
import {ErrorLoggingService} from 'src/app/api/module/erp/shared/services/error-logs/error-logging.service';
import {SpinnerService} from 'src/app/api/module/erp/shared/services/spinner-service.service';
import {AutoSliderItem} from 'src/app/api/module/portal/model/auto-slider';
import {AutoSliderImagesService} from 'src/app/api/module/portal/services/auto-slider-images.service';
import {ProdutoService} from 'src/app/api/module/portal/services/produto/produto.service';

@Component({
  selector: 'app-banner-simulador-preco',
  templateUrl: './banner-simulador-preco.component.html',
  styleUrls: ['./banner-simulador-preco.component.scss'],
})
export class BannerSimuladorPrecoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  dadosOperadoraDM: any[] = [];
  listaProdutos: ProdutoVM[] = [];
  menorValorOdonto!: ProdutoVM;
  menorValorSaude!: ProdutoVM;
  valorAntesDoPontoSaude: string = '';
  valorDepoisDoPontoSaude: string = '';
  valorAntesDoPontoOdonto: string = '';
  valorDepoisDoPontoOdonto: string = '';
  ehLPSimuladorPreco: boolean = false;
  componenteLPSP: boolean = false;
  _tipoProduto_E_OperadoraEnum = TipoProduto_E_OperadoraEnum;
  sliderItemsOdonto: AutoSliderItem[] = [];

  dataInicio: Date;
  dataFim: Date;

  constructor(
    private _compartilharDadosEntreComponentsService: CompartilharDadosEntreComponentsService,
    private _produtoService: ProdutoService,
    private _spinnerService: SpinnerService,
    private _toastrService: ToastrService,
    private _errorLoggingService: ErrorLoggingService,
    private router: Router,
    private route: ActivatedRoute,
    private autoSliderImagesService: AutoSliderImagesService,
  ) {
    this.dataInicio = new Date('2025-03-02T00:00:00');
    this.dataFim = new Date('2025-04-30T23:59:59'); // 30/04/2025 às 23:59:59
  }

  get exibirPublicacao(): boolean {
    const agora = new Date();
    return agora >= this.dataInicio && agora <= this.dataFim;
  }

  ngOnInit(): void {
    this.buscarDados();
    this.verificarRotaAtual();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  iniciarSpinner(mensagem: string = 'Carregando...'): void {
    this._spinnerService.show(mensagem);
  }

  pararSpinner(): void {
    this._spinnerService.hide();
  }

  verificarRotaAtual() {
    const rotaAtual = this.router.url; // Obtém a URL atual

    if (rotaAtual.includes('portal/saude')) {
      this.ehLPSimuladorPreco = false;
    } else if (rotaAtual.includes('lp/fesp/adesao')) {
      this.ehLPSimuladorPreco = true;
    }
  }

  agruparProdutosPorTipo(produtos: any[]): ProdutoVM[] {
    const grupos: {[key: string]: ProdutoVM} = {};

    (produtos || []).forEach(produto => {
      const tipoProduto = produto?.TipoProduto;
      const tipoOperadora = tipoProduto + '-' + produto?.NomeOperadora;

      if (!grupos[tipoOperadora] || produto?.Valor < grupos[tipoOperadora]?.Valor) {
        grupos[tipoOperadora] = new ProdutoVM(
          produto?.IdOperadora,
          produto?.NomeOperadora,
          produto?.Ans,
          produto?.TipoProduto,
          produto?.NomeProduto,
          produto?.De,
          produto?.Ate,
          produto?.Valor,
          produto?.Validade,
        );
      }
    });

    return Object.values(grupos);
  }

  buscarDados() {
    this.iniciarSpinner();
    this._produtoService
      .buscarDadosDaApi()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.pararSpinner();
        }),
      )
      .subscribe({
        next: data => {
          const lista = data?.List || [];
          this.listaProdutos = this.agruparProdutosPorTipo(lista);
          this.menorValorOdonto = this.encontrarMenorValorPorTipo(lista, 'Odonto');
          this.menorValorSaude = this.encontrarMenorValorPorTipo(lista, 'Saúde');

          this.separarValor();
        },
        error: error => {
          this._toastrService.error('Erro ao carregar dados do simulador de preços.');
          this._errorLoggingService.logError('Erro ao buscar dados da API do Simulador', error, {
            contexto: 'BannerSimuladorPrecoComponent.buscarDados',
            data: {
              url: this.router.url,
            },
          });
        },
      });
  }

  encontrarMenorValor(produtos: any[]): ProdutoVM {
    if (!produtos || produtos.length === 0) {
      return null as any;
    }
    return produtos.reduce((minProduto, produto) => {
      if (!minProduto || (produto?.Valor != null && produto.Valor < minProduto.Valor)) {
        return new ProdutoVM(
          produto.IdOperadora,
          produto.NomeOperadora,
          produto.Ans,
          produto.TipoProduto,
          produto.NomeProduto,
          produto.De,
          produto.Ate,
          produto.Valor,
          produto.Validade,
        );
      }
      return minProduto;
    }, null);
  }

  encontrarMenorValorPorTipo(produtos: any[], tipo: string): ProdutoVM {
    if (!produtos || produtos.length === 0) {
      return null as any;
    }
    const produtosFiltrados = produtos.filter(produto => produto?.TipoProduto === tipo);
    if (tipo == 'Saúde') {
      const novaListaSaude = produtosFiltrados.filter(x => x?.NomeOperadora != 'SÃO FRANCISCO VIDA'.trim());
      return this.encontrarMenorValor(novaListaSaude);
    }

    return this.encontrarMenorValor(produtosFiltrados);
  }

  separarValor(): void {
    // Saúde
    if (this.menorValorSaude?.Valor != null) {
      const saude = Number(this.menorValorSaude.Valor).toFixed(2).split('.');
      this.valorAntesDoPontoSaude = saude[0] || '0';
      this.valorDepoisDoPontoSaude = saude[1] || '00';
    } else {
      this.valorAntesDoPontoSaude = '0';
      this.valorDepoisDoPontoSaude = '00';
    }

    // Odonto
    if (this.menorValorOdonto?.Valor != null) {
      const odonto = Number(this.menorValorOdonto.Valor).toFixed(2).split('.');
      this.valorAntesDoPontoOdonto = odonto[0] || '0';
      this.valorDepoisDoPontoOdonto = odonto[1] || '00';
    } else {
      this.valorAntesDoPontoOdonto = '0';
      this.valorDepoisDoPontoOdonto = '00';
    }
  }

  simularPorOperadora(template: TemplateRef<any>, idOperadora?: string, nomeOperadora?: any, tipoPlano: string = 'saude') {
    // Fallback se não veio id direto pelo menor valor
    if (!idOperadora && this.listaProdutos?.length) {
      const tipo = tipoPlano.toLowerCase().includes('odonto') ? 'Odonto' : 'Saúde';
      const fallback = this.listaProdutos.find(p => p.TipoProduto === tipo);
      if (fallback) {
        idOperadora = fallback.IdOperadora;
        nomeOperadora = fallback.NomeOperadora;
      }
    }

    let dadosOperadora: any = {
      idOperadora: idOperadora,
      nomeOperadora: nomeOperadora,
      origem: 'Portal',
      tipoPlano: tipoPlano,
    };

    this.compartilharDados(dadosOperadora);

    // Condição para redirecionamento com base no tipo de URL.
    if (window.location.href.includes('portal/saude')) {
      // Redireciona para a rota padrão
      if (idOperadora) {
        window.open(`#/portal/cotacao/${tipoPlano}/${idOperadora}`, '_self');
      } else {
        window.open(`#/portal/cotacao/${tipoPlano}`, '_self');
      }
    } else if (window.location.href.includes('lp/fesp/adesao')) {
      // Redireciona para a rota especial com parâmetros UTM
      this.componenteLPSP = true;
      this.navegarParaAdesao(idOperadora);
    } else {
      if (idOperadora) {
        window.open(`#/portal/cotacao/${tipoPlano}/${idOperadora}`, '_self');
      } else {
        window.open(`#/portal/cotacao/${tipoPlano}`, '_self');
      }
    }
  }

  navegarParaAdesao(teste: any) {
    // Navega para a mesma URL, mas com parâmetros de query
    this.router.navigate(['lp/fesp/adesao'], {
      queryParams: {
        utm_source: 'adsperformace',
        utm_campaign: 'UnimedFespAdesao',
        utm_medium: 'UnimedFesp',
      },
    });
  }

  private compartilharDados(data: any) {
    this.dadosOperadoraDM = [];
    this.dadosOperadoraDM.push({
      data: data,
    });

    this._compartilharDadosEntreComponentsService &&
      this._compartilharDadosEntreComponentsService.enviarDadosParaOutroComponent(this.dadosOperadoraDM);
  }
}
